import Vue from 'vue'
import store from '@/modules/store'
import BasicCalls from '@/api/models/BasicCalls'
import EventEmitter from 'events'
import loadStripeJs from './loadStripeJs'

const CARD = 'cardNumber'
const EXPIRY = 'cardExpiry'
const CVC = 'cardCvc'
const UNKNOWN = 'unknown'
const MOBILE_PAYMENT = 'paymentRequestButton'
const PAYMENT_EVENT = 'paymentmethod'
class Stripe extends BasicCalls {
    #stripe
    #secretSetupIntent
    #card
    #expiry
    #cvc
    #paymentRequest
    #initialised = false

    constructor() {
        super()
        this._spk = store.getters['site/config']?.stripe_public_key
        this._savedCards = []
        this._selectedCard = null
        this._selectedOnlinePayment = null
        this._loadingDelete = null
        this._loadingSave = false
        this._error = null
        this._onlinePaymentButton = null
        this._elements = null
        this._avaiableOnlinePaymnet = []
        this._event = new EventEmitter()
        this._card = {
            complete: {
                [CARD]: false,
                [EXPIRY]: false,
                [CVC]: false,
            },
            loaded: {
                [CARD]: false,
                [EXPIRY]: false,
                [CVC]: false,
            },
            errors: {
                [CARD]: false,
                [EXPIRY]: false,
                [CVC]: false,
            },
            errorMessage: null,
            brand: UNKNOWN,
        }
    }

    async init() {
        try {
            this.destroy()
            await this.initStripe()
            await this.fetchCardsList()
            return Promise.resolve()
        } catch {
            return Promise.reject('Error during Stripe initialization!')
        }
    }

    async initStripe() {
        if (!this._spk) return this.#stripe
        if (!this.#stripe) {
            const StripeJs = await loadStripeJs()
            if (!this.#stripe)
                this.#stripe = StripeJs(this._spk, {
                    locale: store.getters['locale'],
                })
        }
        return this.#stripe
    }

    async initElements() {
        if (this.#secretSetupIntent && this.elements) return
        try {
            await this.initStripe()
            const setupResult = await this.setupIntent()
            this.#secretSetupIntent = setupResult[0]
            this.elements = this.#stripe.elements({
                clientSecret: this.#secretSetupIntent,
            })
            return
        } catch {
            throw 'Error during stripe element initialisation!'
        }
    }

    async initCard() {
        await this.initElements()
        if (this.elements) {
            this.#card = this.elements.create(CARD)
            this.#expiry = this.elements.create(EXPIRY)
            this.#cvc = this.elements.create(CVC)

            this.#card.mount('#card-number')
            this.#expiry.mount('#card-expiry')
            this.#cvc.mount('#card-cvc')

            this.#card.on('change', (event) => this.#handleChange(event, this))
            this.#expiry.on('change', (event) => this.#handleChange(event, this))
            this.#cvc.on('change', (event) => this.#handleChange(event, this))
            this.#card.on('ready', (event) => this.#handleReady(event, CARD, this))
            this.#expiry.on('ready', (event) => this.#handleReady(event, EXPIRY, this))
            this.#cvc.on('ready', (event) => this.#handleReady(event, CVC, this))
        } else {
            // eslint-disable-next-line no-console
            console.error('Stripe is not initialised!')
        }
    }

    resetCard() {
        ;[CARD, EXPIRY, CVC].forEach((element) => {
            this.#set('complete', element, false)
            this.#set('loaded', element, false)
        })
        this.#card.destroy()
        this.#expiry.destroy()
        this.#cvc.destroy()
        this.#card = null
        this.#expiry = null
        this.#cvc = null
        this.#secretSetupIntent = null
        this.brand = UNKNOWN
    }

    async initOnlinePaymnet() {
        try {
            await this.initElements()
            this.#paymentRequest = await this.#stripe?.paymentRequest(
                this.#getPaymentRequestOptions()
            )
            await this.#canMakePayment()
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e)
        }
    }

    async updateOnlinePaymnet() {
        try {
            if (this.#paymentRequest && !isNaN(this.amount)) {
                await this.#paymentRequest.update(this.#getPaymentRequestTotalOption())
                await this.#canMakePayment()
            }
            return Promise.resolve()
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e)
            return Promise.reject(`Error during online paymnet update! ${e}`)
        }
    }

    #canMakePayment = async function () {
        try {
            let result = await this.#paymentRequest.canMakePayment()
            if (result) {
                this.avaiableOnlinePaymnet = Object.entries(result).reduce((p, [key, value]) => {
                    if (value) p.push(key)
                    return p
                }, [])
            }
            return Promise.resolve()
        } catch {
            return Promise.reject()
        }
    }

    async payOnline($event, secret) {
        await store.dispatch('loading/start')
        try {
            await this.initStripe()
            const confirmResult = await this.#stripe.confirmCardPayment(
                secret,
                { payment_method: $event.paymentMethod.id },
                { handleActions: true }
            )
            if (confirmResult.error) {
                return Promise.reject({ error: confirmResult.error })
            } else {
                $event.complete('success')
                if (confirmResult.paymentIntent.status === 'requires_action') {
                    const actionResult = await this.#stripe.confirmCardPayment(secret)
                    if (actionResult.error) {
                        return Promise.reject(actionResult.error)
                    } else {
                        return Promise.resolve(actionResult)
                    }
                } else {
                    return Promise.resolve(confirmResult)
                }
            }
        } catch (e) {
            return Promise.reject(e)
        } finally {
            await store.dispatch('loading/finish')
        }
    }

    async payViaCard(secret) {
        if (!this.selectedCard) return Promise.reject('No card selected!')
        await store.dispatch('loading/start')
        try {
            await this.initStripe()
            const result = await this.#stripe.confirmCardPayment(secret, {
                payment_method: this.selectedCard,
            })
            if (result.error) return Promise.reject(result.error)
            return Promise.resolve(result)
        } catch (e) {
            return Promise.reject(e)
        } finally {
            await store.dispatch('loading/finish')
        }
    }

    async initOnlinePaymentButton() {
        this.onlinePaymentButton?.destroy()
        this.onlinePaymentButton = this.elements.create(MOBILE_PAYMENT, {
            paymentRequest: this.#paymentRequest,
        })
        await this.onlinePaymentButton.mount('#payment-request-button')
        this.#onPaymentMethod()
    }

    resetOnlinePaymentButton() {
        if (this.onlinePaymentButton) {
            this.onlinePaymentButton.destroy()
            this.onlinePaymentButton = null
            this.#offPaymentMethod()
        }
    }

    async setupIntent() {
        return await this.fetch('payment/stripe/create/setup-intent')
    }

    async fetchCardsList() {
        try {
            this.savedCards = await this.fetch('payment/stripe/getPaymentMethods/card/all')
            return Promise.resolve(this.savedCards)
        } catch (e) {
            return Promise.reject(e)
        }
    }

    async deleteCard(id) {
        try {
            this.loadingDelete = id
            await this.destroy(`payment/stripe/delete-card/${id}`)
            return Promise.resolve()
        } catch (e) {
            return Promise.reject(e)
        } finally {
            this.loadingDelete = null
        }
    }

    async saveCard(name) {
        try {
            this.loadingSave = true
            await this.initStripe()
            const result = await this.#stripe.confirmCardSetup(this.#secretSetupIntent, {
                payment_method: {
                    card: this.#card,
                    billing_details: {
                        name: name,
                    },
                },
            })
            if (result.error) {
                this.error = result.error?.message
                return Promise.reject(result.error)
            }
            return Promise.resolve(result)
        } catch (e) {
            this.error = e?.message
            return Promise.reject(e)
        } finally {
            this.loadingSave = false
        }
    }

    #handleChange = function (event, _this) {
        _this.#set('complete', event.elementType, event?.complete)
        _this.#set('errors', event.elementType, !!event?.error)
        if (event.brand) _this.brand = event.brand
        _this.cardErrorMessage = event?.error?.message
        _this.error = null
    }

    #handleReady = function (event, type, _this) {
        _this.#set('loaded', event.elementType, true)
    }

    #getPaymentRequestOptions = function () {
        const currency = store.getters['active/currency']
        return {
            ...{
                country: currency.country_code.toUpperCase(),
                currency: currency.currency_code.toLowerCase(),
                requestPayerName: true,
                requestPayerEmail: true,
                disableWallets: ['link', 'browserCard'],
            },
            ...this.#getPaymentRequestTotalOption(),
        }
    }

    #getPaymentRequestTotalOption = function () {
        return {
            total: {
                label: 'Total',
                amount: this.amount,
            },
        }
    }

    #offPaymentMethod = function () {
        this.#paymentRequest?.off(PAYMENT_EVENT, this.#emit)
    }

    get amount() {
        return Math.trunc(Vue.prototype.$cart.calculation.price_sum_user * 100)
    }

    #onPaymentMethod = function () {
        this.#paymentRequest.on(PAYMENT_EVENT, this.#emit)
    }

    set brand(val) {
        this._card.brand = val
    }
    get brand() {
        return this._card.brand
    }
    set cardErrorMessage(val) {
        this._card.errorMessage = val
    }
    get cardErrorMessage() {
        return this._card.errorMessage
    }

    #set = function (prop, type, val) {
        Vue.set(this._card[prop], type, val)
    }
    get cardComplete() {
        return !Object.values(this._card.complete).includes(false)
    }

    get cardLoaded() {
        return !Object.values(this._card.loaded).includes(false)
    }

    get cardError() {
        return Object.values(this._card.errors).includes(true)
    }

    get cardErrors() {
        return this._card.errors
    }

    set loadingDelete(val) {
        this._loadingDelete = val
    }
    get loadingDelete() {
        return this._loadingDelete
    }

    set loadingSave(val) {
        this._loadingSave = val
    }
    get loadingSave() {
        return this._loadingSave
    }

    set savedCards(val) {
        this._savedCards = val || []
    }
    get savedCards() {
        return this._savedCards
    }

    set elements(val) {
        this._elements = val
    }
    get elements() {
        return this._elements
    }

    set onlinePaymentButton(val) {
        this._onlinePaymentButton = val
    }
    get onlinePaymentButton() {
        return this._onlinePaymentButton
    }

    set avaiableOnlinePaymnet(val) {
        this._avaiableOnlinePaymnet = val
    }
    get avaiableOnlinePaymnet() {
        return this._avaiableOnlinePaymnet
    }

    set selectedOnlinePayment(val) {
        this._selectedOnlinePayment = val
    }
    get selectedOnlinePayment() {
        return this._selectedOnlinePayment
    }

    set selectedCard(val) {
        this._selectedCard = val
    }
    get selectedCard() {
        return this._selectedCard
    }

    set error(val) {
        this._error = val
    }
    get error() {
        return this._error
    }

    get CARD() {
        return CARD
    }
    get EXPIRY() {
        return EXPIRY
    }
    get CVC() {
        return CVC
    }

    get minimumAmount() {
        return 0.3
    }

    #emit = (payload) => {
        this._event.emit(PAYMENT_EVENT, payload)
    }

    on(listener) {
        if (listener) this._event.on(PAYMENT_EVENT, listener)
        // eslint-disable-next-line no-console
        else console.error('No listener was found!')
    }

    off(listener) {
        if (listener) this._event.off(PAYMENT_EVENT, listener)
        // eslint-disable-next-line no-console
        else console.error('No listener was found!')
    }
}

export default Stripe
